import type { RegisteredRouter, RouteIds } from "@tanstack/react-router"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { ExpandedState, SortingState, Updater } from "@tanstack/react-table"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { RefObject } from "react"
import type { Client } from "urql"

import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import useDebounce from "@/utils/useDebounce"
import { toUpperSnakeCase, upperSnakeCaseToCamelCase } from "@/utils/utils"

interface BaseSearchParams {
  query?: string
  field?: string
  order?: SortingOrder
}

export interface ListPageStoreSelectors<TData, TParams extends BaseSearchParams> {
  data: TData[]
  fetching: boolean
  error: Error | undefined
  executeSearch: (client: Client, params: TParams, force?: boolean) => void
  loadMore: (client: Client, params: TParams) => void
  refetchLoaded?: (client: Client, params: TParams) => Promise<boolean>
  clearStore: () => void
}

interface UseListPageBaseParams<TData, TParams extends BaseSearchParams> {
  route: RouteIds<RegisteredRouter["routeTree"]>
  store: ListPageStoreSelectors<TData, TParams>
  filterKeys?: (keyof TParams)[]
  preservedEmptyFilterKeys?: (keyof TParams)[]
}

export interface UseListPageBaseReturn<TData, TParams extends BaseSearchParams> {
  data: TData[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  expanded: ExpandedState
  searchParams: TParams
  tableContainerRef: RefObject<HTMLDivElement>
  refetch: () => void
  refetchLoaded: () => Promise<boolean>
  handleScroll: () => void
  handleSortingChange: (updater: Updater<SortingState>) => void
  handleExpandedChange: (updater: Updater<ExpandedState>) => void
  handleSearchChange: (query: string) => void
  handleFilterChange: <K extends keyof TParams>(key: K, value: TParams[K]) => void
  handleFiltersChange: (updates: Partial<TParams>) => void
  handleFilterRemove: (keyOrKeys: keyof TParams | (keyof TParams)[], value?: string | number) => void
  handleFilterClear: () => void
}

export const useListPageBase = <TData, TParams extends BaseSearchParams>({
  route,
  store,
  filterKeys = [],
  preservedEmptyFilterKeys = [],
}: UseListPageBaseParams<TData, TParams>): UseListPageBaseReturn<TData, TParams> => {
  const { data, fetching, error, executeSearch, loadMore, refetchLoaded: storeRefetchLoaded, clearStore } = store

  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const handleExpandedChange = useCallback((updater: Updater<ExpandedState>) => setExpanded(updater), [])

  const searchParams = useSearch({ from: route })
  const { field, order } = searchParams
  const navigate = useNavigate({ from: route as any })

  const debouncedQuery = useDebounce(searchParams.query, DEBOUNCE_DELAY_MS)
  const [throttledLoadMore] = useThrottle(() => loadMore(queryClient, searchParams), 200)
  const handleScroll = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const sorting: SortingState = useMemo(
    () => (field ? [{ id: upperSnakeCaseToCamelCase(field), desc: order === SortingOrder.Desc }] : []),
    [field, order]
  )

  useEffect(() => {
    if (searchParams.query !== debouncedQuery) return

    executeSearch(queryClient, { ...searchParams, query: debouncedQuery })
    setExpanded({})
  }, [executeSearch, searchParams, debouncedQuery])

  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  // AK-6649 AC #2: a commit re-applies not-yet-landed values on top of the router's still-stale
  // committed location, so a second same-tick navigate is a superset of the first rather than a
  // replacement that drops it. Cleared once the committed searchParams catch up.
  const pendingSearchRef = useRef<Record<string, unknown>>({})

  useEffect(() => {
    pendingSearchRef.current = {}
  }, [searchParams])

  const refetch = useCallback(() => {
    executeSearch(queryClient, searchParams, true)
    setExpanded({})
  }, [executeSearch, searchParams])

  // Updates the loaded rows in place, unlike `refetch`, which reloads the first page and collapses expanded rows.
  // Resolves to whether the refetched rows were applied; false when the refetch was skipped, failed or is unsupported.
  const refetchLoaded = useCallback(async (): Promise<boolean> => {
    const isRefetched = await storeRefetchLoaded?.(queryClient, { ...searchParams, query: debouncedQuery })

    return isRefetched ?? false
  }, [storeRefetchLoaded, searchParams, debouncedQuery])

  const handleSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const newSorting = typeof updater === "function" ? updater(sorting) : updater

      if (!newSorting.length) return

      const sort = newSorting[0]
      navigate({
        to: ".",
        search: (prev: Record<string, any>) => ({
          ...prev,
          field: toUpperSnakeCase(sort.id),
          order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
        }),
        replace: true,
      })
    },
    [navigate, sorting]
  )

  const handleSearchChange = useCallback(
    (query: string) => {
      navigate({
        to: ".",
        search: (prev: Record<string, any>) => ({
          ...prev,
          query: query || undefined,
        }),
        replace: true,
      })
    },
    [navigate]
  )

  const resolveFilterValue = useCallback(
    <K extends keyof TParams>(key: K, value: TParams[K]): TParams[K] | undefined => {
      const isEmpty = Array.isArray(value) ? value.length === 0 : value === null || value === undefined
      return preservedEmptyFilterKeys.includes(key) || !isEmpty ? value : undefined
    },
    [preservedEmptyFilterKeys]
  )

  const handleFilterChange = useCallback(
    <K extends keyof TParams>(key: K, value: TParams[K]) => {
      pendingSearchRef.current = { ...pendingSearchRef.current, [key]: resolveFilterValue(key, value) }
      navigate({
        to: ".",
        search: (prev: Record<string, any>) => ({ ...prev, ...pendingSearchRef.current }),
        replace: true,
      })
    },
    [navigate, resolveFilterValue]
  )

  const handleFiltersChange = useCallback(
    (updates: Partial<TParams>) => {
      const resolved = (Object.keys(updates) as (keyof TParams)[]).reduce<Record<string, unknown>>((acc, key) => {
        acc[key as string] = resolveFilterValue(key, updates[key] as TParams[keyof TParams])
        return acc
      }, {})
      pendingSearchRef.current = { ...pendingSearchRef.current, ...resolved }
      navigate({
        to: ".",
        search: (prev: Record<string, any>) => ({ ...prev, ...pendingSearchRef.current }),
        replace: true,
      })
    },
    [navigate, resolveFilterValue]
  )

  const handleFilterRemove = useCallback(
    (keyOrKeys: keyof TParams | (keyof TParams)[], value?: string | number) => {
      pendingSearchRef.current = {}
      const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys]
      const updates = keys.reduce<Record<string, unknown>>((acc, key) => {
        const currentValue = searchParams[key]
        const updatedValue =
          Array.isArray(currentValue) && value !== undefined ? currentValue.filter((item) => item !== value) : undefined
        const isEmpty = Array.isArray(updatedValue) ? updatedValue.length === 0 : !updatedValue
        acc[key as string] = preservedEmptyFilterKeys.includes(key) || !isEmpty ? updatedValue : undefined
        return acc
      }, {})
      navigate({
        to: ".",
        search: (prev: Record<string, any>) => ({ ...prev, ...updates }),
        replace: true,
      })
    },
    [navigate, searchParams, preservedEmptyFilterKeys]
  )

  const handleFilterClear = useCallback(() => {
    pendingSearchRef.current = {}
    const clearedFilters = filterKeys.reduce<Record<string, undefined>>(
      (acc, key) => ({ ...acc, [key as string]: undefined }),
      {}
    )
    navigate({
      to: ".",
      search: (prev: Record<string, any>) => ({
        ...prev,
        ...clearedFilters,
      }),
      replace: true,
    })
  }, [navigate, filterKeys])

  return {
    data,
    isFetching: fetching,
    error,
    sorting,
    expanded,
    searchParams,
    tableContainerRef,
    refetch,
    refetchLoaded,
    handleScroll,
    handleSortingChange,
    handleExpandedChange,
    handleSearchChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
  }
}
